import { useState, useEffect } from "react";
import { api } from "../lib/config";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, ScanLine, Bed } from "lucide-react";

/**
 * Login page for a specific role (Kiosco or Logística).
 * If the credentials match a user with the expected role → redirect to
 * /admin/logistica (which already restricts tabs by role).
 * Admin can also use this page and it lands on the same tab set (admin sees all).
 */
export default function RoleLogin({ role }) {
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const config = role === "logistica"
    ? {
        title: "Logística",
        subtitle: "Cabañas y Transporte",
        color: "from-purple-400 to-fuchsia-600",
        Icon: Bed,
        allowed: new Set(["admin", "logistica"]),
        wrongRoleMsg: "Este link es solo para el equipo de Logística.",
      }
    : {
        title: "Kiosco",
        subtitle: "Check-in / Escaneo QR",
        color: "from-blue-400 to-cyan-500",
        Icon: ScanLine,
        allowed: new Set(["admin", "kiosko"]),
        wrongRoleMsg: "Este link es solo para el equipo de Kiosco (Check-in).",
      };

  // If already logged in, verify role and redirect (or bounce out if wrong role).
  useEffect(() => {
    // Update browser tab title
    document.title = `${config.title} · Operación Rescate 2026`;
    const token = localStorage.getItem("admin_token");
    if (!token) return;
    (async () => {
      try {
        const me = await api.get("/admin/me");
        const r = me.data?.role || "admin";
        if (config.allowed.has(r)) {
          nav("/admin/logistica");
        } else {
          toast.error(config.wrongRoleMsg);
          localStorage.removeItem("admin_token");
        }
      } catch {
        localStorage.removeItem("admin_token");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { username, password });
      localStorage.setItem("admin_token", data.token);
      // Look up role and enforce allowed list
      const me = await api.get("/admin/me");
      const r = me.data?.role || "admin";
      if (!config.allowed.has(r)) {
        localStorage.removeItem("admin_token");
        toast.error(config.wrongRoleMsg);
        setLoading(false);
        return;
      }
      toast.success(`Bienvenido · ${config.title}`);
      nav("/admin/logistica");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credenciales inválidas");
    } finally {
      setLoading(false);
    }
  };

  const { Icon } = config;

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6 grain relative">
      <form
        onSubmit={submit}
        data-testid={`role-login-${role}`}
        className="relative max-w-md w-full bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 lg:p-10"
      >
        <div className="flex items-center justify-center mb-6">
          <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${config.color} flex items-center justify-center`}>
            <Icon className="w-7 h-7 text-black" />
          </div>
        </div>
        <h1 className="font-heading uppercase text-3xl text-center text-white mb-2">
          {config.title}
        </h1>
        <p className="text-white/60 text-center text-sm mb-8">
          {config.subtitle} · Operación Rescate 2026
        </p>

        <div className="space-y-4">
          <div>
            <Label htmlFor="user" className="text-white/80">Usuario</Label>
            <Input
              id="user"
              data-testid="role-login-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="mt-2 bg-white/10 border-white/20 text-white placeholder:text-white/40 h-12"
              placeholder={role === "logistica" ? "logistica1" : "kiosko1"}
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="pass" className="text-white/80">Contraseña</Label>
            <Input
              id="pass"
              data-testid="role-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 bg-white/10 border-white/20 text-white placeholder:text-white/40 h-12"
              placeholder="••••••••"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="role-login-submit"
            className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-wider h-12"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
          </Button>
        </div>
      </form>
    </div>
  );
}
